import { createHash } from 'node:crypto';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import {
  MediaWriteService,
  SignedUrlExpiry,
  StorageBuckets,
} from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

import { findExistingUploadedMedia } from './dancer-media-finalize-support';
import { getSignedUrl } from './dancer-media-signed-urls';
import type { DancerMediaContext } from './dancer-media.context';
import {
  buildMediaStoragePath,
  resolveAllowedMimeTypes,
  resolveMaxUploadSize,
} from './dancer-media.mappers';
import type { DancerMedia, MediaUploadInput } from './dancer-media.schema';

/**
 * Upload media file to storage and create database record
 * Upload a media file (headshot, photo, or video) to dancer-media storage
 * and create the matching artist_media row. For external video URLs
 * (YouTube/Vimeo), use addVideoLink() instead.
 */
export async function uploadMedia(
  ctx: DancerMediaContext,
  userId: string,
  file: File,
  input: MediaUploadInput,
): Promise<Result<DancerMedia>> {
  const logContext = {
    method: 'uploadMedia',
    userId,
    mediaType: input.mediaType,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
  };

  ctx.log('info', 'Starting media upload', logContext);

  try {
    const maxSize = resolveMaxUploadSize(input.mediaType);

    if (file.size > maxSize) {
      ctx.log('warn', 'File size exceeds limit', {
        ...logContext,
        maxSize,
      });
      return Result.fail(
        ServiceError.validation(
          `File size exceeds ${maxSize / (1024 * 1024)}MB limit`,
        ),
      );
    }

    const { allowedTypes, allowedDescription } = resolveAllowedMimeTypes(
      input.mediaType,
    );

    if (!allowedTypes.includes(file.type)) {
      ctx.log('warn', 'Invalid file type', {
        ...logContext,
        allowedTypes,
      });
      return Result.fail(
        ServiceError.validation(
          `Invalid file type. Allowed: ${allowedDescription}`,
        ),
      );
    }

    // Validate headshot uniqueness
    if (input.mediaType === 'headshot') {
      const { data: existingHeadshot } = await ctx.client
        .from('artist_media')
        .select('id')
        .eq('user_id', userId)
        .eq('media_type', 'headshot')
        .maybeSingle();

      if (existingHeadshot) {
        ctx.log('warn', 'Headshot already exists', {
          ...logContext,
          existingHeadshotId: existingHeadshot.id,
        });
        return Result.fail(
          ServiceError.validation(
            'Headshot already exists. Please delete the existing one first.',
          ),
        );
      }
    }

    // Validate video count cap: 5 videos total per user (combined
    // YouTube/Vimeo links + uploaded files). Mirrors the same limit
    // enforced in addVideoLink() below.
    if (input.mediaType === 'video') {
      const { data: existingVideos } = await fetchAllRows((from, to) =>
        ctx.client
          .from('artist_media')
          .select('id')
          .eq('user_id', userId)
          .eq('media_type', 'video')
          .order('id')
          .range(from, to),
      );

      if (existingVideos && existingVideos.length >= 5) {
        ctx.log('warn', 'Max videos reached', {
          ...logContext,
          existingVideoCount: existingVideos.length,
        });
        return Result.fail(
          ServiceError.validation('Maximum 5 videos allowed per profile.'),
        );
      }
    }

    // Content hash (BAL-700): the server receives the raw bytes on this
    // path, so compute the md5 directly — matches the S3 single-PUT ETag
    // that finalizeDirectUpload reads from storage, so both upload paths
    // populate content_hash identically. Persisted on the row and enforced
    // by idx_artist_media_unique_content_hash (same-bytes dedup even under
    // a filename change).
    const contentHash = createHash('md5')
      .update(Buffer.from(await file.arrayBuffer()))
      .digest('hex');

    // Idempotent re-upload guard (BAL-700): a caller re-uploading a file it
    // already uploaded (double-click, retry-after-timeout, drag the same
    // file twice) previously always inserted a new artist_media row with a
    // distinct storage_path. Return the existing live row instead of
    // creating a duplicate. Matches on filename OR identical bytes
    // (content_hash) so a re-upload saved under a different name is caught
    // too. Bounded to at most two lookups, no N+1.
    const existingMedia = await findExistingUploadedMedia(
      ctx,
      userId,
      input.mediaType,
      file.name,
      contentHash,
    );
    if (existingMedia) {
      ctx.log('info', 'Duplicate upload detected — returning existing row', {
        ...logContext,
        existingMediaId: existingMedia.id,
      });
      const existingSignedUrlResult = existingMedia.storage_path
        ? await getSignedUrl(
            ctx,
            existingMedia.storage_path,
            SignedUrlExpiry.IMMEDIATE_DISPLAY,
          )
        : null;
      const existingDisplayUrl = existingSignedUrlResult?.isSuccess
        ? existingSignedUrlResult.getValue()
        : null;
      return Result.ok({
        ...existingMedia,
        storage_path: existingDisplayUrl || existingMedia.storage_path,
      });
    }

    const storagePath = buildMediaStoragePath(
      userId,
      input.mediaType,
      file.name,
    );

    ctx.log('info', 'Uploading file to storage', {
      ...logContext,
      storagePath,
    });

    // Upload bytes then insert the row atomically. MediaWriteService rolls
    // back the just-uploaded object if the insert fails (no orphan object).
    // The row payload (incl. the professional_profile_id lookup) is built
    // INSIDE writeRow so it runs after the upload, preserving the original
    // upload-then-record ordering.
    const recordResult = await new MediaWriteService(
      ctx.client,
    ).uploadAndRecord<Database['public']['Tables']['artist_media']['Row']>({
      bucket: StorageBuckets.DANCER_MEDIA,
      path: storagePath,
      body: file,
      uploadOptions: { contentType: file.type, upsert: false },
      writeRow: async (verifiedPath) => {
        ctx.log('info', 'Creating database record', {
          ...logContext,
          userId,
          storagePath: verifiedPath,
          mediaType: input.mediaType,
        });

        // The row contract (columns, professional_profile_id link, headshot
        // is_primary + profile stamp) lives in the shared
        // finalize_dancer_media_upload RPC (BAL-603, Rule 8) — the same call
        // finalizeDirectUpload and the mobile app make.
        const rpcArgs = {
          p_user_id: userId,
          p_media_type: input.mediaType,
          p_storage_path: verifiedPath,
          p_file_name: file.name,
          p_file_size_bytes: file.size,
          p_mime_type: file.type,
          p_content_hash: contentHash,
          p_title: input.title || undefined,
          p_description: input.description || undefined,
        };

        const { data: media, error: dbError } = await ctx.client.rpc(
          'finalize_dancer_media_upload',
          rpcArgs,
        );

        if (dbError || !media) {
          ctx.log('error', 'Database insert failed', {
            ...logContext,
            error: dbError?.message,
            errorCode: dbError?.code,
            errorDetails: dbError?.details,
            errorHint: dbError?.hint,
            rpcArgs,
          });
          return Result.fail(
            ServiceError.database(
              dbError?.message ?? 'Failed to save media',
              dbError,
            ),
          );
        }

        return Result.ok(media);
      },
    });

    if (recordResult.isFailure) {
      return Result.fail(recordResult.getError());
    }

    const media = recordResult.getValue();

    ctx.log('info', 'Media upload completed successfully', {
      ...logContext,
      mediaId: media.id,
    });

    // Generate signed URL for immediate display in the uploader
    const signedUrlResult = await getSignedUrl(
      ctx,
      storagePath,
      SignedUrlExpiry.IMMEDIATE_DISPLAY,
    );
    const displayUrl = signedUrlResult.isSuccess
      ? signedUrlResult.getValue()
      : null;

    // Return media with signed URL as storage_path for client display
    return Result.ok({
      ...media,
      storage_path: displayUrl || storagePath,
    });
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'uploadMedia',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}
